import { z } from 'zod';

// src/enums.ts
var TIERS = ["basic", "premium", "fighter"];
var ROLES = ["patient", "doctor", "command", "analyst"];
var APP_SCOPES = ["cc-public", "cc-patient", "cc-doctor", "cc-command"];
var APPOINTMENT_STATUSES = [
  "scheduled",
  "confirmed",
  "live",
  "completed",
  "cancelled",
  "no_show"
];
var APPOINTMENT_LABELS = ["follow_up", "new_visit", "general", "check_in"];
var EMR_SYNC_STATUSES = ["pending", "synced", "failed"];
var APPLICATION_STATUSES = ["submitted", "in_review", "approved", "rejected"];
var tierSchema = z.enum(TIERS);
var roleSchema = z.enum(ROLES);
var appScopeSchema = z.enum(APP_SCOPES);
var appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
var appointmentLabelSchema = z.enum(APPOINTMENT_LABELS);
var emrSyncStatusSchema = z.enum(EMR_SYNC_STATUSES);
var applicationStatusSchema = z.enum(APPLICATION_STATUSES);
var ROLE_TO_SCOPE = {
  patient: "cc-patient",
  doctor: "cc-doctor",
  command: "cc-command",
  analyst: "cc-command"
};
var ENUMS = {
  Tier: TIERS,
  Role: ROLES,
  AppScope: APP_SCOPES,
  AppointmentStatus: APPOINTMENT_STATUSES,
  AppointmentLabel: APPOINTMENT_LABELS,
  EmrSyncStatus: EMR_SYNC_STATUSES,
  ApplicationStatus: APPLICATION_STATUSES
};
var healthieSsoHandoffSchema = z.object({
  user_id: z.string(),
  healthie_user_id: z.string(),
  oidc_id_token: z.string(),
  redirect_uri: z.string().url(),
  expires_at: z.string().datetime()
});
var zoomMeetingJoinSchema = z.object({
  zoom_meeting_id: z.string(),
  zoom_join_url: z.string().url(),
  zoom_start_url: z.string().url(),
  passcode: z.string()
});
var huddleNoteSchema = z.object({
  id: z.string().optional(),
  appointment_id: z.string(),
  doctor_id: z.string(),
  patient_id: z.string(),
  body: z.string().min(1).max(4e3),
  created_at: z.string().datetime().optional()
});
var visitSummarySchema = z.object({
  patient_id: z.string(),
  last_visit_at: z.string().datetime(),
  summary_text: z.string().max(280),
  healthie_appointment_id: z.string()
});
var doctorCredentialsSchema = z.object({
  npi: z.string(),
  dea_number: z.string().nullable().optional(),
  // nullable/off by default (B7/O3 — CC never prescribes)
  license_state: z.string(),
  license_number: z.string(),
  license_expires_at: z.string().datetime(),
  malpractice_carrier: z.string().optional(),
  malpractice_policy_no: z.string().optional(),
  malpractice_expires_at: z.string().datetime().optional(),
  agreement_version: z.string().optional(),
  agreement_accepted_at: z.string().datetime().optional(),
  baa_accepted_at: z.string().datetime().optional()
});
var doctorPatientAssignmentSchema = z.object({
  doctor_id: z.string(),
  patient_id: z.string(),
  assigned_at: z.string().datetime().optional(),
  assigned_by: z.string().optional(),
  status: z.enum(["active", "inactive"]).default("active")
});
var availabilityExceptionSchema = z.object({
  doctor_id: z.string(),
  starts_at: z.string().datetime(),
  ends_at: z.string().datetime(),
  kind: z.enum(["pto", "block", "extra"]),
  reason: z.string().optional()
});
var notificationPreferenceSchema = z.object({
  user_id: z.string(),
  channel: z.enum(["email", "push"]),
  opted_in: z.boolean(),
  quiet_hours_start: z.string().optional(),
  quiet_hours_end: z.string().optional(),
  locale: z.string().optional()
});
var pushDeviceTokenSchema = z.object({
  user_id: z.string(),
  platform: z.enum(["ios", "android"]),
  token: z.string()
});
var walletPassSchema = z.object({
  patient_id: z.string(),
  provider: z.enum(["apple", "google"]),
  serial: z.string(),
  issued_at: z.string().datetime().optional(),
  revoked_at: z.string().datetime().nullable().optional()
});
var rescheduleRequestSchema = z.object({
  appointment_id: z.string(),
  new_scheduled_at: z.string().datetime(),
  reason: z.string().min(1)
});
var cancelRequestSchema = z.object({
  appointment_id: z.string(),
  reason: z.string().min(1)
});

// src/redaction.ts
var REDACTED = "[REDACTED]";
var SENSITIVE_KEY_PATTERNS = [
  // Class-B free text
  /(^|_)body$/i,
  /message/i,
  /summary/i,
  /note/i,
  // Class-C identifiers
  /name/i,
  /email/i,
  /phone/i,
  /dob|date_?of_?birth/i,
  /address/i,
  /emergency/i,
  /insurance/i,
  /member_?ref/i,
  /passcode/i,
  // credentials / secrets
  /token/i,
  /secret/i,
  /password/i,
  /authorization/i,
  /cookie/i,
  /api_?key/i,
  /signature/i,
  /set-cookie/i
];
function isSensitiveKey(key) {
  return SENSITIVE_KEY_PATTERNS.some((re) => re.test(key));
}
function redactString(input) {
  if (typeof input !== "string") return input;
  return input.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, REDACTED).replace(/\+?\d[\d ().-]{8,}\d/g, REDACTED).replace(/\b\d{6,}\b/g, REDACTED);
}
function redactDeep(value, depth = 0) {
  if (depth > 6) return REDACTED;
  if (value == null) return value;
  if (typeof value === "string") return redactString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = isSensitiveKey(k) ? REDACTED : redactDeep(v, depth + 1);
    }
    return out;
  }
  return REDACTED;
}
function sentryBeforeSend(event) {
  if (!event || typeof event !== "object") return event;
  if (event.message) event.message = redactString(event.message);
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.query_string;
    if (event.request.headers) {
      for (const k of Object.keys(event.request.headers)) {
        if (isSensitiveKey(k)) event.request.headers[k] = REDACTED;
      }
    }
  }
  if (event.extra) event.extra = redactDeep(event.extra);
  if (event.contexts) event.contexts = redactDeep(event.contexts);
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      message: b.message ? redactString(b.message) : b.message,
      data: b.data ? redactDeep(b.data) : b.data
    }));
  }
  if (event.user) {
    const id = event.user.id;
    event.user = id == null ? {} : { id: redactString(String(id)) };
  }
  return event;
}
var CLINICAL_PATTERNS = [
  /\bsoap\b/i,
  /\bdiagnos[ei]s\b/i,
  /\bdiagnoses\b/i,
  /\brx\b/i,
  /\bprescri/i,
  /\bdosage\b/i,
  /\bmg\b/i,
  /\ballerg/i,
  /\blab(s| result)/i,
  /\bvital sign/i,
  /\ba1c\b/i,
  /\bbp\b/i,
  /\bicd-?\d/i
];
function looksClinical(text) {
  return CLINICAL_PATTERNS.some((re) => re.test(text));
}

export { APPLICATION_STATUSES, APPOINTMENT_LABELS, APPOINTMENT_STATUSES, APP_SCOPES, EMR_SYNC_STATUSES, ENUMS, ROLES, ROLE_TO_SCOPE, SENSITIVE_KEY_PATTERNS, TIERS, appScopeSchema, applicationStatusSchema, appointmentLabelSchema, appointmentStatusSchema, availabilityExceptionSchema, cancelRequestSchema, doctorCredentialsSchema, doctorPatientAssignmentSchema, emrSyncStatusSchema, healthieSsoHandoffSchema, huddleNoteSchema, isSensitiveKey, looksClinical, notificationPreferenceSchema, pushDeviceTokenSchema, redactDeep, redactString, rescheduleRequestSchema, roleSchema, sentryBeforeSend, tierSchema, visitSummarySchema, walletPassSchema, zoomMeetingJoinSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map